<template>
  <ion-modal
    :is-open="visible"
    css-class="update-required-modal"
    :backdrop-dismiss="!pending"
    @did-dismiss="onDidDismiss"
  >
    <UpdateRequiredModal
      :current-version="APP_VERSION"
      :new-version="publishedVersion"
      :blocking="BLOCKING"
      :pending="pending"
      @update="onUpdate"
      @later="onLater"
    />
  </ion-modal>
</template>

<script setup lang="ts">
/**
 * Host for the version watchdog's "Update available" modal.
 *
 * A modal rather than a banner, unlike the SW update prompt: this fires only
 * when the *server* says a newer build is deployed and the service worker did
 * not (or could not) tell us — the client is genuinely behind, possibly by a
 * long way, and a dismissible strip at the bottom of the screen is too easy to
 * never notice. When both signals fire, this wins and the banner hides
 * (UpdatePromptHost checks `updateAvailable`).
 *
 * Mounted in App.vue, so it survives every navigation. Deliberately allowed on
 * the public routes too (sign-in / onboarding): pre-auth is the safest possible
 * moment to reload, and a stale SW-less client is very likely to be sitting
 * exactly there. That is only acceptable because the modal is dismissible.
 *
 * FUTURE — forced updates. Flip {@link BLOCKING} to true and: "Later" hides via
 * the prop, set `:backdrop-dismiss="false"`, add
 * `:can-dismiss="() => !updateAvailable"` (Ionic's Android hardware-back handler
 * ignores backdrop-dismiss and only respects can-dismiss — see
 * SurveyPromptHost), and revisit the public-route allowance above.
 */
import { computed, ref, watch } from 'vue';
import { IonModal } from '@ionic/vue';
import { useRoute } from 'vue-router';
import UpdateRequiredModal from '@/components/modals/UpdateRequiredModal.vue';
import { useToast } from '@/composables/useToast';
import { updateInProgress } from '@/pwa/update-session';
import { applyPublishedUpdate, publishedVersion, updateAvailable } from '@/pwa/version-check';
import { useSurveysStore } from '@/stores/surveys.store';
import { isComposerRoute } from '@/utils/tab-bar';
import { APP_VERSION } from '@/utils/app-info';

/** Ships off: the update is offered, never imposed. See the note above. */
const BLOCKING = false;

const surveysStore = useSurveysStore();
const route = useRoute();
const toaster = useToast();

const pending = ref(false);
/**
 * "Later" only lasts for this page session. The published build doesn't go
 * away, so the next cold start (or a newer deploy, via the watch below) brings
 * the modal back — no persistence needed, and no way to permanently miss it.
 */
const dismissed = ref(false);

// A newer deploy arriving after a dismissal re-arms the modal.
watch(updateAvailable, (now, was) => {
  if (now && !was) dismissed.value = false;
});

const visible = computed(
  () =>
    updateAvailable.value &&
    !dismissed.value &&
    // Never stack over a required survey — this computed re-fires by itself
    // when the queue drains, and the version fetch already puts first detection
    // well past first paint, so no settle timer is needed.
    !surveysStore.current &&
    // Never cover a half-typed message or assistant prompt.
    !isComposerRoute(route.path) &&
    // The update wall is up — this modal has handed over to it.
    !updateInProgress.value,
);

function onLater(): void {
  dismissed.value = true;
}

/** Backdrop tap. Ignored while updating — the modal is `backdrop-dismiss=false` then. */
function onDidDismiss(): void {
  if (!pending.value) dismissed.value = true;
}

async function onUpdate(): Promise<void> {
  if (pending.value) return;
  pending.value = true;
  try {
    // 'started': the "Updating Syntry…" wall (UpdateProgressHost) has taken
    // over and the page reloads out from under it. 'offline': nothing was
    // touched — the device is offline, or the update server did not answer —
    // and the modal is still offered.
    const result = await applyPublishedUpdate();
    if (result === 'offline') {
      await toaster.error("You're offline. Connect to the internet, then tap Update now again.");
    }
  } finally {
    pending.value = false;
  }
}
</script>
